import * as React from 'react';
import { FlatList, Image, View, StyleSheet, useWindowDimensions } from 'react-native';
import * as AppUtilities from "../context/AppUtilities";
import * as AppConstants from "../context/AppConstants";
import * as AppState from "../context/AppState";
import { AppContext } from "../context/AppContext";
import { useSelector, useDispatch } from 'react-redux';
import { Text, Divider } from 'react-native-paper';
import Markdown from 'react-native-markdown-display-fh';
import { CustomButton } from '../components/CustomButton';
import { CustomChip } from '../components/CustomChip';
import { SearchLauncher } from '../components/SearchLauncher';
import * as AppCache from "../context/AppCache";

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    flexGrow: 1,
  },
  item: {
    marginVertical: 4,
  },
  emptyView: {
    padding: 20,
  },
  mainView: {
    backgroundColor: "#ffffff",
    margin: 5,
    borderWidth: 1,
    borderTopWidth: 5,
    borderColor: "#e4e9f2",
  },
  link: {
    color: "#0000EE",
    textDecorationLine: "underline",
    //paddingTop: 10,
    //padding: 20,
    //paddingLeft: 25,
  },
  descriptionView: {
    padding: 10,
    paddingLeft: 25,
    paddingBottom: 10,
    paddingTop: 0,
    flexDirection: "row",
  },
  titleWrapper: {
    padding: 20,
    paddingLeft: 25,
    paddingTop: 10,
    paddingBottom: 10,
  },
  descriptionSplit: {
    flex: 1,
    flexDirection: "row",
  },
  descriptionRight: {
    width: 10,
    padding: 100,
  },
  certificationImage: {
    width: 85,
    marginLeft: 15,
    marginBottom: 5,
  },
  certifications: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 10,
    maxWidth: 100,
  },
  certificationsIntro: {
    marginTop: 20,
  },
  listProductsButton: {
    paddingTop: 10,
    paddingBottom: 10,
    //backgroundColor: "#ffffff"
  },
  searchWrapper: {
    padding: 10,
    minHeight: 75,
    //maxHeight: 300,
  },
  introWrapper: {
    padding: 10,
  },
  chip: {
    marginTop: 20,
  },
  rightColumn: {
    flexDirection: "column",
    flexShrink: 1,
    justifyContent: "flex-start",
    alignItems: "flex-end",
  },
});

const markdownStyles = StyleSheet.create({
  heading1: {
    paddingBottom: 10,
  },
  heading2: {
    paddingVertical: 10,
  },
});

// https://github.com/iamacup/react-native-markdown-display/blob/master/src/lib/renderRules.js
const markdownRules = {
  text: (node, children, parent, styles, inheritedStyles = {}) => (
    <Text key={node.key} style={[inheritedStyles, styles.text]} selectable={true}>
      {node.content}
    </Text>
  ),
};

function shouldShow(certification, width) {
  if (width < AppConstants.SMALL_SCREEN_WIDTH1) {
    if (certification == "agw_certified_regenerative" ||
        certification == "agw_certified_grassfed" ||
        certification == "agw_certified_non_gmo" ||
        certification == "agw_certified_organic" ||
        certification == "applegate_humanely_raised") {
      return false;
    }
  }
  return true;
}

export function Delivery({ navigation }) {
  const { showPermanentSnack, showTemporarySnack, hideSnack, getLoginState, showSpinner, hideSpinner } = React.useContext(AppContext);
  const locations_online = useSelector((state) => state.locations_online);
  const deliveryLocationFilter = useSelector((state) => state.deliveryLocationFilter);
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(true);
  const { width, height } = useWindowDimensions();
  const dataFilter = useSelector((state) => state.dataFilter);
  const [isFocused, setIsFocused] = React.useState(false);
  const lastProcessedDataFilterID = React.useRef(null);

  React.useEffect(() => {
    if (navigation) {
      return navigation.addListener("focus", () => {
        AppUtilities.info("Delivery focus");
        AppUtilities.recordAppPageView("delivery");
        setIsFocused(true);
      });
    }
  }, [navigation]);

  React.useEffect(() => {
    if (navigation) {
      return navigation.addListener("blur", () => {
        AppUtilities.info("Delivery blur");
        setIsFocused(false);
      });
    }
  }, [navigation]);

  async function onLoad() {
    try {
      showSpinner();
      hideSnack();

      AppUtilities.info("Delivery.onLoad");

      if (getLoginState() == AppConstants.LOGIN_STATE.LOGGED_IN_LOCALLY ||
          getLoginState() == AppConstants.LOGIN_STATE.LOGGED_IN_REMOTELY) {

        let locationsJSON = null;
        if (AppUtilities.isDefaultFilter(dataFilter)) {

          const defaultLink = AppUtilities.getDefaultFilterLink(dataFilter);

          AppUtilities.info("Delivery.onLoad using default filter: " + defaultLink);

          // See if it's in the cache
          locationsJSON = await AppCache.getCachedDefaultData(defaultLink);

          if (!locationsJSON) {

            const response = await AppUtilities.fetchUnauthorized(
              "Delivery.onLoad",
              AppUtilities.getMainDataLink(dataFilter),
              "GET",
            );
    
            if (response.body) {
              locationsJSON = response.body;
            }
          } else {
            AppUtilities.info("Delivery.onLoad found default filter in cache");
          }

          AppUtilities.info("Delivery.onLoad default filter load completed");
        } else {
          const response = await AppUtilities.fetchUnauthorized(
            "Delivery.onLoad",
            AppUtilities.getMainDataLink(dataFilter),
            "GET",
          );
  
          if (response.body) {
            locationsJSON = response.body;
          }
        }

        if (locationsJSON) {
          hideSpinner();
          lastProcessedDataFilterID.current = dataFilter[AppConstants.SYNTHESIZED_TYPES.QUERY_ID[AppConstants.TYPE_KEYS.URL_KEY]];

          dispatch(AppState.createDispatch(AppConstants.DISPATCH.SET_INITIAL_DATA, {
            locations: AppUtilities.postProcessResultsLocations(locationsJSON),
            locations_online: locationsJSON.locations_online,
            ads: locationsJSON.ads,
          }));

          setLoading(false);
        } else {
          throw new Error("Result body not found");
        }
      }

      await AppState.checkAndHandleInitialLoad(showTemporarySnack, () => navigation.navigate(AppConstants.SCREENS.LEARN));
    } catch (e) {
      showPermanentSnack(AppUtilities.getErrors(e), "Retry", () => onLoad());
      hideSpinner();
      AppUtilities.handleException(e, "Delivery.onLoad");
    }
  }

  React.useEffect(() => {
    if (dataFilter && isFocused && lastProcessedDataFilterID.current != dataFilter[AppConstants.SYNTHESIZED_TYPES.QUERY_ID[AppConstants.TYPE_KEYS.URL_KEY]]) {
      onLoad();
    }
  }, [dataFilter, isFocused]);

  const renderItem = (row) => {
    const certification_images = [];
    Object.keys(row.item.certifications).forEach((key) => {
      if (row.item.certifications[key] > 0 &&
          key != "self_certified" &&
          key != "usda_organic" &&
          key != "american_grassfed_association" &&
          key != "applegate_humanely_raised" &&
          shouldShow(key, width)
          ) {
        certification_images.push(
          <Image
            key={key}
            source={AppUtilities.certificationLogos[AppUtilities.certificationLogoOffsets[key]]}
            style={styles.certificationImage}
          />
        );
      }
    });

    let descriptionMarkdown = row.item.onlinedescription;

    if (row.item.deliversto) {
      descriptionMarkdown += "\n\n**Delivers to:** " + row.item.deliversto;
    }
  
    const products = Object.keys(row.item.products);
    if (products.length > 0) {
      const product_types = [];
      products.forEach((key) => {
        if (row.item.products[key] > 0) {
          for (const productType of Object.values(AppConstants.PRODUCT_TYPES)) {
            if (productType[AppConstants.TYPE_KEYS.URL_KEY] == key) {
              product_types.push(productType[AppConstants.TYPE_KEYS.TITLE]);
            }
          }
        }
      });
      if (product_types.length > 0) {
        //descriptionMarkdown += "\n\n**Certified products:** " + product_types.join(", ");
      }
    }

    let linkToGoTo = row.item.website_underlying ? row.item.website_underlying : row.item.website;
    if (!row.item.linknoutm) {
      linkToGoTo = AppUtilities.getLinkWithUTM(linkToGoTo, AppConstants.UTM_CAMPAIGNS.DELIVERY);
    }

    return (
      <View style={styles.mainView}>
        <View style={styles.titleWrapper}>
          {
            AppUtilities.createInternalLink(
              navigation,
              <Text
                variant="headlineMedium"
                style={{
                  //paddingBottom: 10
                }}
              >
                {row.item.title}
              </Text>,
              AppConstants.SCREENS.LOCATION_ONLINE_DETAILS,
              {
                locationid: row.item.id,
                online: "show"
              },
              {
                textDecoration: "none",
                //paddingBottom: 10,
              },
            )
          }
          {
            //AppUtilities.createLink(row.item.website, linkToGoTo, AppConstants.UTM_CAMPAIGNS.DELIVERY, null, true)
          }
        </View>
        <Divider />
        <View style={styles.descriptionView}>
          <View style={{ flex: 1, flexWrap: 'wrap' }}>
            <Markdown style={markdownStyles} rules={markdownRules}>
              {
                descriptionMarkdown
              }
            </Markdown>
            {
              AppUtilities.isWeb() && false ?
                  AppUtilities.createInternalLink(
                    navigation,
                    <CustomButton
                      style={styles.listProductsButton}
                      appearance="outline"
                    >
                      List certified products
                    </CustomButton>,
                    AppConstants.SCREENS.LOCATION_ONLINE_DETAILS,
                    {
                      locationid: row.item.id,
                      online: "show"
                    },
                    {
                      textDecoration: "none",
                    },
                  )
                :
                  <CustomButton
                    //style={styles.listProductsButton}
                    mode="outlined"
                    appearance="outline"
                    onPress={() => navigation.navigate(AppConstants.SCREENS.LOCATION_ONLINE_DETAILS, {
                      locationid: row.item.id,
                      online: "show"
                    })}
                  >
                    List certified products
                  </CustomButton>
            }
          </View>
          <View style={styles.rightColumn}>
            {
              certification_images.length > 0 ?
                  <>
                    {/*<Text style={styles.certificationsIntro}>
                      Certified products are{certification_images.length > 1 ? " one or more of" : ""}:
                    </Text>*/}
                    <View style={styles.certifications}>
                      {
                        certification_images
                      }
                    </View>
                  </>
                :
                  null
            }
          </View>
        </View>
      </View>
    );
  };

  let finalProcessedLocations = locations_online ? locations_online : [];
  if (deliveryLocationFilter && deliveryLocationFilter.length > 0) {
    finalProcessedLocations = finalProcessedLocations.filter((item) => (
      item.title == deliveryLocationFilter
    ));
  }

  return (
    <FlatList
      keyboardShouldPersistTaps="handled"
      contentContainerStyle={styles.contentContainer}
      data={finalProcessedLocations}
      //initialNumToRender={AppUtilities.isWeb() ? finalProcessedLocations.length : 10}
      renderItem={renderItem}
      ListEmptyComponent={() => {
        if (loading) return null;
        return (
          <View style={styles.emptyView}>
            <Text variant="bodyMedium">Nothing found</Text>
          </View>
        );
      }}
      ListFooterComponent={(
        <>
          {
            finalProcessedLocations && finalProcessedLocations.length > 1 ?
                <View style={{ padding: 20 }}>
                  <Text>{finalProcessedLocations.length > 0 ? "Results: " + finalProcessedLocations.length : ""}</Text>
                </View>
              :
                null
          }
        </>
      )}
      ListHeaderComponent={(
        <>
          <View style={{ padding: 10 }}>
            <SearchLauncher floating={false} hideSearchLocation={true} />
            {
              deliveryLocationFilter && deliveryLocationFilter.length > 0 ?
                  <CustomChip
                    onClose={() => dispatch(AppState.createDispatch(AppConstants.DISPATCH.SET_DELIVERY_LOCATION_FILTER, ""))}
                    style={styles.chip}
                  >{deliveryLocationFilter}</CustomChip>
                :
                  null
            }
          </View>
        </>
      )}
    />
  );
}
